import {
  Badge,
  Button,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr
} from '@chakra-ui/react'
import { useEffect, useState, useRef } from 'react'

import Paginator from '../../../components/paginator'
import { formatDate, formatDateTime } from '../../../helpers'
import { DutyPlannerService } from '../../../services/DutyPlanner/DutyPlannerService'
import { DutyPlannerListType as DutyPlannerListType } from '../../../services/DutyPlanner/types'
import DutyPlannerDeleteDialog from './DutyPlannerDeleteDialog'
import DutyPlannerFormModal from './DutyPlannerFormModal'

const timeToEvent = (date: string) => {
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  const eventDate = new Date(date)
  const diff = eventDate.getTime() - now.getTime()

  if (diff >= 0) {
    const diffInDays = Math.ceil(diff / (1000 * 3600 * 24))

    if (diffInDays == 0) {
      return <Badge colorScheme="magenta">hoje</Badge>
    }

    if (diffInDays == 1) {
      return <Badge colorScheme="yellow">amanhã</Badge>
    }

    return (
      <Badge colorScheme="green">
        em {diffInDays} dia{diffInDays > 1 ? 's' : ''}
      </Badge>
    )
  }

  return ''
}

export default function DutyPlannerList({
  title,
  orderBy,
  show
}: {
  title: string
  orderBy: { key: string; direction: 'asc' | 'desc' }
  show: 'upcoming' | 'past'
}) {
  const service = new DutyPlannerService()
  const [loading, setLoading] = useState(false)
  const [duties, setDuties] = useState<DutyPlannerListType>({
    data: [],
    total: 0,
    current_page: 1,
    last_page: 1,
    per_page: 10
  })
  const [editing, setEditing] = useState<number | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [deleting, setDeleting] = useState<number | null>(null)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [page, setPage] = useState(1)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const [search, setSearch] = useState('')

  useEffect(() => {
    loadDuties()
  }, [])

  useEffect(() => {
    loadDuties()
  }, [page, search, orderBy, show])

  const loadDuties = async () => {
    setLoading(true)
    service.list({ search, orderBy, show, pageSize: 10, page }).then((data) => {
      setDuties(data)
      setLoading(false)
    })
  }

  return (
    <>
      <Heading as="h2" size="md" mb="10" textTransform="uppercase">
        {title}
      </Heading>
      <Flex flex="1" justifyContent="flex-end" alignItems="center">
        <FormLabel htmlFor="search" fontSize="sm" fontWeight="normal" p={0} m={0} mr={2}>
          Pesquisar
        </FormLabel>
        <Input
          type="search"
          size="sm"
          variant="outline"
          id="search"
          w="200px"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Flex>
      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Título</Th>
              <Th>Data</Th>
              <Th>Dia da semana</Th>
              <Th>Possui plantão?</Th>
              <Th>Criado em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading && (
              <Tr>
                <Td colSpan={7} textAlign="center">
                  Carregando...
                </Td>
              </Tr>
            )}
            {duties.data.length === 0 ? (
              <Tr>
                <Td colSpan={7} textAlign="center">
                  Nenhum registro encontrado
                </Td>
              </Tr>
            ) : (
              duties.data.map((duty) => (
                <Tr key={duty.id}>
                  <Td>{duty.id}</Td>
                  <Td>{duty.title}</Td>
                  <Td>
                    {formatDate(duty.date.split('T')[0] + 'T00:00:00')} {timeToEvent(duty.date)}
                  </Td>
                  <Td>
                    {new Date(duty.date.split('T')[0] + 'T00:00:00')
                      .toLocaleDateString('pt-BR', { weekday: 'long' })
                      .replace(/^./, (str) => str.toUpperCase())}
                  </Td>
                  <Td>
                    {duty.has_duty ? (
                      <>
                        <Badge colorScheme="green">Sim</Badge>
                        <Tooltip
                          label={duty.consultants.map((consultant) => (
                            <Text key={consultant.name}>- {consultant.name}</Text>
                          ))}
                          hasArrow
                        >
                          <Badge colorScheme="green" ml="2">
                            {duty.consultants.length} consultor{duty.consultants.length == 1 ? '' : 'es'}
                          </Badge>
                        </Tooltip>
                      </>
                    ) : (
                      <Badge colorScheme="red">Não</Badge>
                    )}
                  </Td>
                  <Td>{formatDateTime(duty.created_at, { showSeconds: true })}</Td>
                  <Td>
                    <Tooltip label="Editar">
                      <IconButton
                        aria-label="Editar"
                        icon={<i className="lni lni-pencil" />}
                        colorScheme="blue"
                        size="sm"
                        mr={2}
                        onClick={() => {
                          setEditing(duty.id)
                          setIsFormOpen(true)
                        }}
                      />
                    </Tooltip>
                    <Tooltip label="Excluir">
                      <IconButton
                        aria-label="Excluir"
                        icon={<i className="lni lni-trash-can" />}
                        size="sm"
                        colorScheme="red"
                        onClick={() => {
                          setDeleting(duty.id)
                          setIsDeleteOpen(true)
                        }}
                      />
                    </Tooltip>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <Paginator
        page={page}
        totalPages={duties.last_page}
        changePage={(page: number) => {
          setPage(page)
        }}
      />

      <DutyPlannerFormModal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false)
          loadDuties()
        }}
        id={editing}
      />
      {deleting && (
        <DutyPlannerDeleteDialog
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          id={deleting}
          leastDestructiveRef={cancelRef}
          onDelete={() => {
            setIsDeleteOpen(false)
            loadDuties()
          }}
        />
      )}

      <Tooltip label="Adicionar " aria-label="Adicionar " placement="left">
        <Button
          colorScheme="magenta"
          position="fixed"
          bottom="20px"
          right="20px"
          borderRadius="full"
          width="70px"
          height="70px"
          onClick={() => {
            setEditing(null)
            setIsFormOpen(true)
          }}
        >
          <i className="lni lni-plus"></i>
        </Button>
      </Tooltip>
    </>
  )
}
